import React, { useEffect } from "react";
import "./Modal.css";

interface ModalProps {
  isOpen: boolean;
  title: string;
  description?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel: () => void;
  disableConfirm?: boolean;
  children?: React.ReactNode; // Allows the modal to display child elements
  swapKeyboardActions?: boolean; // Add this prop to swap keyboard bindings
  hideButtons?: boolean;
  customContent?: React.ReactNode;
  isVideoModal?: boolean; // Add this prop to control video modal styling
  zIndex?: number;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  description,
  confirmText = "",
  cancelText = "Cancel",
  onConfirm = () => {},
  onCancel,
  disableConfirm,
  children,
  swapKeyboardActions,
  hideButtons,
  customContent,
  isVideoModal = false,
  zIndex,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        swapKeyboardActions ? onConfirm() : onCancel();
      } else if (e.key === "Enter" && !disableConfirm) {
        // Prevent triggering if user is typing in an input field
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          swapKeyboardActions ? onCancel() : onConfirm();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onCancel, onConfirm, disableConfirm, swapKeyboardActions]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={zIndex !== undefined ? { zIndex } : undefined}>
      <div className={`modal ${isVideoModal ? 'video-modal' : ''}`}>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="Close">
          <span className="modal-cancel-text">Cancel (ESC)</span>
          <img src="/assets/x.svg" alt="" aria-hidden="true" className="modal-close-icon" />
        </button>
        {title && <h2>{title}</h2>}
        {description && (typeof description === "string" ? <p>{description}</p> : description)}
        {customContent ? (
          <div className="modal-content">{customContent}</div>
        ) : children && (
          <div className="modal-content">{children}</div>
        )}
        {!hideButtons && (
          <div className="modal-actions">
            {confirmText && <button className="button-primary" onClick={onConfirm} disabled={disableConfirm}>{confirmText}</button>}
            <button className="button-secondary" onClick={onCancel}>{cancelText}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
